/**
 * Pure chord identification: a set of MIDI notes → chord quality + root +
 * bass + inversion.
 *
 * Scope (Phase 2A C.2):
 *   - Triads (maj, min, dim, aug)
 *   - Sevenths (maj7, dom7, min7, min7b5, dim7)
 *
 * Out of scope:
 *   - Extended chords (9, 11, 13) — identifying a 5+ note set without key
 *     context is genuinely ambiguous (e.g. {C, E, G, B, D} is Cmaj9 OR a
 *     Dm/C-style upper-structure reading). The chord-quality generator
 *     plays these in Practice but Phase 2A C.2 doesn't grade them; the
 *     unsupported-exercise hint kicks in.
 *   - Altered dominants (7♭9 / 7♯9 / 7♯11 / 7♭13) — same reasoning.
 *   - Slash chords — the bass-as-non-chord-tone case explodes the matching
 *     surface. Document as a follow-up.
 *
 * Algorithm:
 *   1. Reduce the input to unique pitch classes (mod 12). 3 pcs → try
 *      triads; 4 pcs → try sevenths; otherwise return null.
 *   2. For each candidate root (one per unique pc), compute the set of
 *      intervals (mod 12) from that root to every other pc. Match against
 *      the known patterns by set-equality.
 *   3. For each match, compute the inversion = which chord tone the bass
 *      (lowest sounding note) corresponds to.
 *   4. Pick the candidate that produces the LOWEST inversion index, with
 *      "candidate root pc == bass pc" as a tie-break. This is the
 *      conventional reading for symmetric chords (dim7, aug) where every
 *      rotation matches the pattern — by music-theory convention, the
 *      bass IS the root unless a specific context overrides.
 *
 * The algorithm is octave-agnostic in matching but inversion-aware via
 * the bass note. So a C major triad played C4-E4-G4 and one played
 * C5-E4-G3 both identify as {quality: "maj", inversion: 0 if bass = C,
 * inversion 1 if bass = E, inversion 2 if bass = G}.
 */

const TRIAD_PATTERNS: ReadonlyArray<readonly [string, readonly number[]]> = [
  ["maj", [0, 4, 7]],
  ["min", [0, 3, 7]],
  ["dim", [0, 3, 6]],
  ["aug", [0, 4, 8]],
];

const SEVENTH_PATTERNS: ReadonlyArray<readonly [string, readonly number[]]> = [
  ["maj7",   [0, 4, 7, 11]],
  ["min7",   [0, 3, 7, 10]],
  ["dom7",   [0, 4, 7, 10]],
  ["min7b5", [0, 3, 6, 10]],
  ["dim7",   [0, 3, 6, 9]],
];

export type ChordQuality =
  | "maj" | "min" | "dim" | "aug"
  | "maj7" | "min7" | "dom7" | "min7b5" | "dim7";

export interface IdentifiedChord {
  quality: ChordQuality;
  /** Root MIDI value, anchored to the same octave as the bass note. */
  rootMidi: number;
  /** Lowest sounding note across all input MIDI values. */
  bassMidi: number;
  /**
   * Which chord tone is in the bass: 0 = root, 1 = third, 2 = fifth,
   * 3 = seventh. (Triads cap at 2; only sevenths reach 3.)
   */
  inversion: 0 | 1 | 2 | 3;
}

/**
 * Identify the triad or 7th chord that best fits a set of MIDI notes.
 *
 * Returns null when:
 *   - Fewer than 3 unique pitch classes are present (just an interval
 *     or a single note's octave doublings).
 *   - More than 4 unique pitch classes (extended chord — out of C.2 scope).
 *   - No known pattern matches any candidate root.
 *
 * Note doublings are tolerated — {C3, E3, G3, C4} identifies as Cmaj
 * inv 0 the same way {C3, E3, G3} does.
 *
 * @param notes - MIDI note numbers, any order. Empty or single-note input → null.
 */
export function identifyChord(notes: ReadonlyArray<number>): IdentifiedChord | null {
  if (notes.length < 3) return null;

  const uniquePcs = new Set<number>();
  for (const n of notes) {
    uniquePcs.add(((n % 12) + 12) % 12);
  }
  const pcCount = uniquePcs.size;
  if (pcCount < 3 || pcCount > 4) return null;

  const sorted = [...notes].sort((a, b) => a - b);
  const bassMidi = sorted[0]!;
  const bassPc = ((bassMidi % 12) + 12) % 12;

  const patterns = pcCount === 3 ? TRIAD_PATTERNS : SEVENTH_PATTERNS;

  type Match = {
    quality: ChordQuality;
    rootPc: number;
    inversion: 0 | 1 | 2 | 3;
  };
  const matches: Match[] = [];

  for (const candidateRoot of uniquePcs) {
    // Compute the candidate's interval set (relative to itself).
    const intervals = new Set<number>();
    for (const pc of uniquePcs) {
      intervals.add(((pc - candidateRoot) % 12 + 12) % 12);
    }
    for (const [quality, pattern] of patterns) {
      if (pattern.length !== intervals.size) continue;
      let allMatch = true;
      for (const iv of pattern) {
        if (!intervals.has(iv)) {
          allMatch = false;
          break;
        }
      }
      if (!allMatch) continue;

      // Where does the bass sit in this pattern?
      const bassOffset = ((bassPc - candidateRoot) % 12 + 12) % 12;
      const inversionIdx = pattern.indexOf(bassOffset);
      if (inversionIdx < 0) continue; // shouldn't happen — bassPc is in intervals
      matches.push({
        quality: quality as ChordQuality,
        rootPc: candidateRoot,
        inversion: inversionIdx as 0 | 1 | 2 | 3,
      });
    }
  }

  if (matches.length === 0) return null;

  // Score: lower inversion wins; tie-break preferring `rootPc == bassPc`
  // (the natural reading for symmetric chords like dim7 / aug).
  matches.sort((a, b) => {
    if (a.inversion !== b.inversion) return a.inversion - b.inversion;
    const aBassRoot = a.rootPc === bassPc ? 0 : 1;
    const bBassRoot = b.rootPc === bassPc ? 0 : 1;
    return aBassRoot - bBassRoot;
  });
  const best = matches[0]!;

  // Anchor the root to the bass's octave. The exact octave doesn't matter
  // for grading downstream, but a stable choice keeps test assertions sane.
  const bassOctave = Math.floor(bassMidi / 12);
  let rootMidi = bassOctave * 12 + best.rootPc;
  if (rootMidi > bassMidi) {
    rootMidi -= 12;
  }

  return {
    quality: best.quality,
    rootMidi,
    bassMidi,
    inversion: best.inversion,
  };
}
